/**
 * Client-side split/merge for result_chunks (mirrors firebase/results_chunking.py).
 * MongoDB: 16 MB/doc max; limits below replace legacy Firestore ~1 MB / 800 KB chunk caps.
 */

const MIB = 1024 * 1024;

export const MONGODB_MAX_BYTES = 16 * MIB;
export const MAIN_DOC_SAFE_BYTES = 12 * MIB;
export const CHUNK_SAFE_BYTES = 14 * MIB;
export const ARRAY_SPLIT_TARGET_BYTES = 7 * MIB;
export const ORIGINAL_PLAN_TARGET_BYTES = 6 * MIB;
export const RESULT_CHUNKS_SUBCOLLECTION = 'result_chunks';
export const RESULTS_STORAGE_VERSION = 2;

const INLINE_FIELD_KEYS = new Set(['summary', 'api_usage', 'gis_api_usage', 'isPublished']);
const CHUNKABLE_FIELD_KEYS = new Set([
  'drivers',
  'unassigned_trips',
  'original_plan',
  'multiload_routes',
  'driver_eligibility',
  'metadata',
]);

export const estimateJsonBytes = (obj) => {
  try {
    return new Blob([JSON.stringify(obj)]).size;
  } catch {
    return JSON.stringify(obj).length * 2;
  }
};

const chunkFieldSuffix = (field, index, total) => {
  if (total <= 1) return field;
  return `${field}__${index}`;
};

/** Session-scoped chunk id — prevents cross-run overwrites in shared _chunks collection. */
export const chunkDocId = (resultId, field, index, total) => {
  return `${resultId}__${chunkFieldSuffix(field, index, total)}`;
};

/** True when heavy results may live in result_chunks, regardless of storage version. */
export const sessionNeedsChunks = (docData) => {
  if (docData?.has_result_chunks) return true;
  return Object.keys(docData?.result_chunks || {}).length > 0;
};

const driverIdentity = (bucket) => {
  const driver = bucket?.driver;
  if (driver && typeof driver === 'object') {
    const did = driver._id ?? driver.id;
    if (did != null) return String(did);
  }
  for (const key of ['driver_id', '_id', 'id']) {
    if (bucket?.[key] != null) return String(bucket[key]);
  }
  return String(Math.random());
};

/** Phase 4: split trips[] inside one oversized driver bucket. */
export const expandBucketsBySplittingTrips = (buckets, tripsKey = 'trips', targetBytes = ORIGINAL_PLAN_TARGET_BYTES) => {
  const expanded = [];
  const tripTarget = Math.max(100_000, Math.floor(targetBytes / 2));

  for (const bucket of buckets || []) {
    if (!bucket || typeof bucket !== 'object') {
      expanded.push(bucket);
      continue;
    }
    if (estimateJsonBytes(bucket) <= CHUNK_SAFE_BYTES) {
      expanded.push(bucket);
      continue;
    }
    const trips = bucket[tripsKey] || [];
    if (!trips.length) {
      expanded.push(bucket);
      continue;
    }
    const wrapper = Object.fromEntries(Object.entries(bucket).filter(([k]) => k !== tripsKey));
    const tripSlices = splitArrayIntoChunks('__trips', trips, tripTarget);
    for (const spec of tripSlices) {
      const partBucket = { ...wrapper, [tripsKey]: spec.data };
      if (estimateJsonBytes(partBucket) <= CHUNK_SAFE_BYTES) {
        expanded.push(partBucket);
      } else {
        for (const trip of spec.data) {
          expanded.push({ ...wrapper, [tripsKey]: [trip] });
        }
      }
    }
  }
  return expanded;
};

export const mergeDriverBucketsByIdentity = (buckets, tripsKey = 'trips') => {
  const byId = new Map();
  const order = [];
  for (const bucket of buckets || []) {
    if (!bucket || typeof bucket !== 'object') continue;
    const did = driverIdentity(bucket);
    const trips = bucket[tripsKey] || [];
    if (!byId.has(did)) {
      byId.set(did, { ...bucket, [tripsKey]: [...trips] });
      order.push(did);
    } else {
      byId.get(did)[tripsKey].push(...trips);
    }
  }
  return order.map((id) => byId.get(id));
};

export const splitArrayIntoChunks = (field, items, targetBytes = ARRAY_SPLIT_TARGET_BYTES) => {
  if (!items?.length) {
    return [{ field, chunk_index: 0, chunk_count: 1, data: [], byte_size: 2 }];
  }

  const rawChunks = [];
  let current = [];
  let currentSize = 0;

  for (const item of items) {
    const itemSize = estimateJsonBytes(item);
    if (current.length && currentSize + itemSize > targetBytes) {
      rawChunks.push(current);
      current = [];
      currentSize = 0;
    }
    if (itemSize > CHUNK_SAFE_BYTES) {
      if (current.length) {
        rawChunks.push(current);
        current = [];
        currentSize = 0;
      }
      if (item && typeof item === 'object' && 'trips' in item) {
        expandBucketsBySplittingTrips([item], 'trips', targetBytes).forEach((part) => rawChunks.push([part]));
      } else {
        rawChunks.push([item]);
      }
      continue;
    }
    current.push(item);
    currentSize += itemSize;
  }
  if (current.length) rawChunks.push(current);

  const total = rawChunks.length;
  return rawChunks.map((data, chunk_index) => ({
    field,
    chunk_index,
    chunk_count: total,
    data,
    byte_size: estimateJsonBytes(data),
  }));
};

export const splitFieldIntoChunks = (field, value) => {
  const size = estimateJsonBytes(value);

  if (INLINE_FIELD_KEYS.has(field)) {
    if (size <= CHUNK_SAFE_BYTES) return { inline: value, chunks: [] };
    return {
      inline: null,
      chunks: [{ field, chunk_index: 0, chunk_count: 1, data: value, byte_size: size }],
    };
  }

  if (!CHUNKABLE_FIELD_KEYS.has(field)) {
    if (size <= CHUNK_SAFE_BYTES) return { inline: value, chunks: [] };
    return {
      inline: null,
      chunks: [{ field, chunk_index: 0, chunk_count: 1, data: value, byte_size: size }],
    };
  }

  if (Array.isArray(value)) {
    let items = value;
    if (field === 'drivers') {
      items = expandBucketsBySplittingTrips(value, 'trips', ORIGINAL_PLAN_TARGET_BYTES);
    }
    return { inline: null, chunks: splitArrayIntoChunks(field, items) };
  }

  return {
    inline: null,
    chunks: [{ field, chunk_index: 0, chunk_count: 1, data: value, byte_size: size }],
  };
};

export const buildChunkManifest = (chunkSpecs, resultId) => {
  const byField = {};
  for (const spec of chunkSpecs) {
    if (!byField[spec.field]) byField[spec.field] = [];
    byField[spec.field].push(spec);
  }

  const manifest = {};
  for (const [field, specs] of Object.entries(byField)) {
    specs.sort((a, b) => a.chunk_index - b.chunk_index);
    manifest[field] = {
      storage: 'subcollection',
      docs: specs.map((s) => chunkDocId(resultId, s.field, s.chunk_index, s.chunk_count)),
      byte_size: specs.reduce((sum, s) => sum + (s.byte_size || 0), 0),
      chunk_count: specs.length,
    };
  }
  return manifest;
};

export const mergeChunkDocuments = (chunkDocs) => {
  const byField = {};
  for (const docData of chunkDocs) {
    const field = docData.field;
    if (!field) continue;
    if (!byField[field]) byField[field] = [];
    byField[field].push(docData);
  }

  const merged = {};
  for (const [field, docs] of Object.entries(byField)) {
    docs.sort((a, b) => (a.chunk_index || 0) - (b.chunk_index || 0));
    if (docs.length === 1) {
      merged[field] = docs[0].data;
      continue;
    }

    const mergeMode = docs[0].merge_mode;
    if (field === 'original_plan' || mergeMode === 'original_plan') {
      const allBuckets = [];
      const allUnassigned = [];
      for (const d of docs) {
        const part = d.data || {};
        allBuckets.push(...(part.drivers || []));
        allUnassigned.push(...(part.unassigned_trips || []));
      }
      merged[field] = {
        drivers: mergeDriverBucketsByIdentity(allBuckets, 'trips'),
        unassigned_trips: allUnassigned,
      };
    } else if (field === 'drivers' || mergeMode === 'planner_drivers') {
      const allDrivers = [];
      for (const d of docs) {
        if (Array.isArray(d.data)) allDrivers.push(...d.data);
      }
      merged[field] = mergeDriverBucketsByIdentity(allDrivers, 'trips');
    } else {
      const data = [];
      for (const d of docs) {
        if (Array.isArray(d.data)) data.push(...d.data);
        else if (d.data != null) data.push(d.data);
      }
      merged[field] = data;
    }
  }
  return merged;
};

export const isV2Document = (docData) => docData?.results_storage_version === RESULTS_STORAGE_VERSION;

export const isLegacyFullResults = (docData) => {
  if (isV2Document(docData)) return false;
  if (docData?.has_result_chunks || docData?.result_chunks) return false;
  const results = docData?.results || {};
  return Boolean(results.drivers || results.original_plan);
};

export const mergeInlineAndChunks = (inlineResults, chunkMerged) => ({
  ...(inlineResults || {}),
  ...chunkMerged,
});
